import {
  type Ref,
  type Component,
  ref,
  computed,
  readonly,
  shallowRef,
} from "vue";
import {
  defineStore,
} from "pinia";

const DEFAULT_TELEPORTED_TO = "body";
const DEFAULT_WIDTH_VALUE = "40%";
const DEFAULT_HEIGHT_VALUE = "";
const DEFAULT_MIN_WIDTH_VALUE = "40%";
const DEFAULT_MIN_HEIGHT_VALUE = "40%";
const DEFAULT_MAX_WIDTH_VALUE = "90%";
const DEFAULT_MAX_HEIGHT_VALUE = "90%";
const DEFAULT_ALLOW_CLOSE_BY_ESCAPE = false;
const DEFAULT_SHOW_CLOSE = true;
const DEFAULT_ALIGN_CENTER = true;
const DEFAULT_ON_OPEN_VALUE = () => {};
const DEFAULT_ON_CLOSE_VALUE = () => {};
const DEFAULT_SETUP_FN_VALUE = () => {};
const DEFAULT_BEFORE_CLOSE_VALUE = (done: () => void) => {
  done();
};

export const DEFAULT_IDENTIFIER = 0;
type TeleportTarget = string | HTMLElement;

type AbstractDialogConfigType = {
  titleComponent: Component | null;
  bodyComponent: Component | null;
  footerComponent: Component | null;
  options?: {
    dialogWidth?: string;
    dialogHeight?: string;
    dialogMinWidth?: string;
    dialogMinHeight?: string;
    dialogMaxWidth?: string;
    dialogMaxHeight?: string;
    dialogClass?: string;
    /** Set false to let a tall dialog start at the top and scroll with the overlay. */
    alignCenter?: boolean;
    allowCloseByEscape?: boolean;
    showClose?: boolean;
    teleportedTo?: TeleportTarget;
    onOpen?: typeof DEFAULT_ON_OPEN_VALUE;
    onClose?: typeof DEFAULT_ON_CLOSE_VALUE;
    setupFn?: typeof DEFAULT_SETUP_FN_VALUE;
    beforeClose?: typeof DEFAULT_BEFORE_CLOSE_VALUE;
  };
};

export type AbstractDialogInstanceType = {
  identifier: number;
  titleComponent: Component | null;
  bodyComponent: Component | null;
  footerComponent: Component | null;
  dialogWidth: string;
  dialogHeight: string;
  dialogMinWidth: string;
  dialogMinHeight: string;
  dialogMaxWidth: string;
  dialogMaxHeight: string;
  dialogClass: string;
  alignCenter: boolean;
  allowCloseByEscape: boolean;
  showClose: boolean;
  teleportedTo: TeleportTarget;
  onOpen: typeof DEFAULT_ON_OPEN_VALUE;
  onClose: typeof DEFAULT_ON_CLOSE_VALUE;
  setupFn: typeof DEFAULT_SETUP_FN_VALUE;
  beforeClose: typeof DEFAULT_BEFORE_CLOSE_VALUE;
  isVisible: boolean;
  activateFocusTrap: boolean;
};

export function vModelMapping<T>(propName: string, propReference: Ref<T>) {
  return {
    [propName]: propReference.value,
    [`onUpdate:${propName}`]: (value: T) => {
      propReference.value = value;
    },
  };
}

export const useDialogStoreV2 = defineStore("dialogStoreV2", () => {
  const instances = ref<AbstractDialogInstanceType[]>([]);
  const activeIdentifiers = ref<AbstractDialogInstanceType["identifier"][]>([]);
  const defaultTeleportedTo = shallowRef<TeleportTarget>(DEFAULT_TELEPORTED_TO);
  const activeIdentifier = computed<number>(() => {
    return activeIdentifiers.value[activeIdentifiers.value.length - 1] || DEFAULT_IDENTIFIER;
  });

  return {
    instances: readonly(instances),
    activeIdentifier,

    showDialog,
    hideDialog,

    destroyDialog,

    activateDialog,
    deactivateDialog,

    // @todo: transform later to abstract "updateDialog"
    setAllowCloseByEscape,
    setDefaultTeleportedTo,
  };

  function showDialog(config: AbstractDialogConfigType) {
    const instance = initializeDialog(config);

    return {
      hideDialog: hideDialog.bind(null, instance.identifier),
      setAllowCloseByEscape: setAllowCloseByEscape.bind(null, instance.identifier),
      setActivateFocusTrap: setActivateFocusTrap.bind(null, instance.identifier),
    };
  }

  function hideDialog(identifier: number = activeIdentifier.value) {
    updateInstance(instances, identifier, (element) => {
      element.isVisible = false;
    });
  }

  function activateDialog(identifier: number = activeIdentifier.value) {
    activeIdentifiers.value = [
      ...activeIdentifiers.value,
      identifier,
    ];
  }

  function deactivateDialog(identifier: number = activeIdentifier.value) {
    activeIdentifiers.value = activeIdentifiers.value.filter((element) => element !== identifier);
  }

  function initializeDialog(config: AbstractDialogConfigType) {
    const instance: AbstractDialogInstanceType = {
      identifier: Math.random(),
      titleComponent: config.titleComponent,
      bodyComponent: config.bodyComponent,
      footerComponent: config.footerComponent,
      dialogWidth: config.options?.dialogWidth || DEFAULT_WIDTH_VALUE,
      dialogHeight: config.options?.dialogHeight || DEFAULT_HEIGHT_VALUE,
      dialogMinWidth: config.options?.dialogMinWidth === "" ? config.options?.dialogMinWidth : (config.options?.dialogMinWidth || DEFAULT_MIN_WIDTH_VALUE),
      dialogMinHeight: config.options?.dialogMinHeight === "" ? config.options?.dialogMinHeight : (config.options?.dialogMinHeight || DEFAULT_MIN_HEIGHT_VALUE),
      dialogMaxWidth: config.options?.dialogMaxWidth === "" ? config.options?.dialogMaxWidth : (config.options?.dialogMaxWidth || DEFAULT_MAX_WIDTH_VALUE),
      dialogMaxHeight: config.options?.dialogMaxHeight === "" ? config.options?.dialogMaxHeight : (config.options?.dialogMaxHeight || DEFAULT_MAX_HEIGHT_VALUE),
      dialogClass: config.options?.dialogClass || "",
      alignCenter: config.options?.alignCenter ?? DEFAULT_ALIGN_CENTER,
      allowCloseByEscape: config.options?.allowCloseByEscape ?? DEFAULT_ALLOW_CLOSE_BY_ESCAPE,
      showClose: config.options?.showClose ?? DEFAULT_SHOW_CLOSE,
      teleportedTo: config.options?.teleportedTo ?? defaultTeleportedTo.value,
      onOpen: config.options?.onOpen || DEFAULT_ON_OPEN_VALUE,
      onClose: config.options?.onClose || DEFAULT_ON_CLOSE_VALUE,
      setupFn: config.options?.setupFn || DEFAULT_SETUP_FN_VALUE,
      beforeClose: config.options?.beforeClose || DEFAULT_BEFORE_CLOSE_VALUE,
      isVisible: true,
      activateFocusTrap: true,
    };

    instances.value = [
      ...instances.value,
      instance,
    ];

    return instance;
  }

  function destroyDialog(identifier: number = activeIdentifier.value) {
    instances.value = instances.value.filter((element) => element.identifier !== identifier);
  }

  function setAllowCloseByEscape(identifier: number = activeIdentifier.value, newAllowCloseByEscape: boolean) {
    updateInstance(instances, identifier, (element) => {
      element.allowCloseByEscape = newAllowCloseByEscape;
    });
  }

  function setDefaultTeleportedTo(newTeleportedTo: TeleportTarget) {
    defaultTeleportedTo.value = newTeleportedTo;
  }

  function setActivateFocusTrap(identifier: number = activeIdentifier.value, newActivateFocusTrap: boolean) {
    updateInstance(instances, identifier, (element) => {
      element.activateFocusTrap = newActivateFocusTrap;
    });
  }
});

function updateInstance(holder: Ref<AbstractDialogInstanceType[]>, identifier: AbstractDialogInstanceType["identifier"], action: (element: AbstractDialogInstanceType) => void) {
  const targetInstance = holder.value.find((element) => element.identifier === identifier);

  if (!targetInstance) {
    return;
  }

  action(targetInstance);
}
